import { Add, Delete, Edit } from '@mui/icons-material';
import { Button, Divider, Grid, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useContext, useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Button as SMButton,
  Loader,
  OrderManagementPage,
  PageTitle,
  Pagination,
  SearchSection,
  Table
} from '../../../components';
import { UserContext } from '../../../context';
import {
  deletePlaceOrderUser,
  getAllReportsTo,
  getPlaceOrderRoles,
  getPlaceOrderUsers
} from '../../../fetch';
import {
  IManagePlaceOrderReportsTo,
  IManagePlaceOrderRole,
  IManagePlaceOrderUser
} from '../../../models';
import { alphaSort } from '../../../helpers';
import { MobileUserRoles } from './MobileUserRoles';
import { UserFilters } from './UserFilters';
import { UserRolesUserModal } from './UserRolesUserModal';
import { useFlags } from '../../../hooks';

export const ManageUsers = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { user, isGlobalAdmin, isAdmin, isBillingAdmin, isFinancialAdmin, canEdit } =
    useContext(UserContext);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [isLoadingRoles, setLoadingRoles] = useState<boolean>(true);
  const [isDeleting, setDeleting] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<IManagePlaceOrderUser | null>(null);
  const [roles, setRoles] = useState<IManagePlaceOrderRole[] | null>(null);
  const [reportsToUsers, setReportsToUsers] = useState<IManagePlaceOrderReportsTo[] | null>(null);
  const [isLoadingReportsTo, setLoadingReportsTo] = useState<boolean>(true);
  const [selectedRoleId, setSelectedRoleId] = useState<string | number>('');
  const [selectedReportsToAccount, setSelectedReportsToAccount] = useState<string>('');
  const [filters, setFilters] = useState<{
    roleId?: number | string;
    query?: string;
    reportsToAccountName?: string;
  }>({});
  const [searchValue, setSearch] = useState<string>('');
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [isUserModalOpen, showUserModal] = useState<boolean>(false);
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const isMobile = useMediaQuery('(max-width: 960px)');
  const featureFlags = useFlags();

  const fetchRoles = async () => {
    try {
      setLoadingRoles(true);
      const roles = await getPlaceOrderRoles();
      setRoles(alphaSort(roles.records, 'name'));
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingRoles(false);
    }
  };

  const fetchReportsToUsers = async () => {
    try {
      setLoadingReportsTo(true);
      const res = await getAllReportsTo();
      setReportsToUsers(res);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingReportsTo(false);
    }
  };

  const [isLoadingManagePlaceOrderUsers, setLoadingManagePlaceOrderUsers] = useState<boolean>(true);
  const [managePlaceOrderUsers, setManagePlaceOrderUsers] = useState<IManagePlaceOrderUser[]>([]);
  const fetchUsers = async () => {
    try {
      setLoadingManagePlaceOrderUsers(true);
      const res = await getPlaceOrderUsers({
        page: page + 1,
        perPage: perPage,
        query: searchedValue,
        ...filters
      });
      setManagePlaceOrderUsers(res.records);
      setRecordCount(res.totalRecordCount);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'We were unable to load users. Please try again.', {
        variant: 'error'
      });
    } finally {
      setLoadingManagePlaceOrderUsers(false);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchReportsToUsers();
  }, []);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, filters, searchedValue]);

  const handleEditUsers = async (user: IManagePlaceOrderUser) => {
    setCurrentUser(user);
    showUserModal(true);
  };

  const handleDelete = async (accountName: string) => {
    const result = window.confirm('Are you sure you want to delete this user?');
    if (result) {
      try {
        setDeleting(true);
        await deletePlaceOrderUser(accountName);
        enqueueSnackbar('User deleted!', {
          variant: 'success'
        });
        fetchUsers();
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? 'Unable to delete user, please try again.', {
          variant: 'error'
        });
      } finally {
        setDeleting(false);
      }
    }
  };

  const columns = [
    {
      Header: 'Name',
      accessor: 'displayName',
      sort: false
    },
    {
      Header: 'Role',
      accessor: 'placeOrderRole',
      sort: false
    },
    {
      Header: 'Reports To',
      accessor: 'reportsTo',
      sort: false
    },
    {
      id: 'actions',
      accessor: '',
      Header: '',
      overrideWidth: isGlobalAdmin || isAdmin ? 210 : null,
      className: classes.editButton,
      sort: false,

      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: IManagePlaceOrderUser } };
      }) => {
        return (
          (isGlobalAdmin || isAdmin) && (
            <>
              <SMButton
                id="edit"
                color="secondary"
                startIcon={<Edit />}
                onClick={async () => {
                  handleEditUsers(original);
                }}
              >
                Edit
              </SMButton>
              <SMButton
                id="delete"
                color="error"
                startIcon={<Delete />}
                onClick={async () => {
                  handleDelete(original.accountName);
                }}
              >
                Delete
              </SMButton>
            </>
          )
        );
      }
    }
  ];

  if (!featureFlags.authorizeUserNavigationAccess){
    if (
      user &&
      user.userRoles &&
      !user.userRoles.includes('BL_Admin') &&
      !isBillingAdmin &&
      !isFinancialAdmin
    ) {
      return <Redirect to={'/production/orders/order-management'} />;
    }
  }

  return (
    <>
      {isDeleting && (
        <Loader position="centered" type="fullscreen">
          Deleting...
        </Loader>
      )}
      <OrderManagementPage title="Manage Users">
        <Grid container>
          <Grid item xs={12} className={classes.pageTitleWrapper}>
            <PageTitle showDivider={false} title="Manage Users" />
          </Grid>
          <Grid item className={classes.infoWrapper}>
            <Grid item xs={12}>
              {(isGlobalAdmin || isAdmin) && (
                <Button
                  id="add-user"
                  onClick={() => {
                    showUserModal(true);
                  }}
                  startIcon={<Add />}
                  color="primary"
                  variant="contained"
                >
                  Add User
                </Button>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Divider />
        <UserFilters
          isLoading={isLoadingManagePlaceOrderUsers || isLoadingRoles || isLoadingReportsTo}
          setSelectedRoleId={setSelectedRoleId}
          selectedRoleId={selectedRoleId}
          setSelectedReportsToAccount={setSelectedReportsToAccount}
          selectedReportsToAccount={selectedReportsToAccount}
          roles={roles}
          hasAppliedFilters={hasAppliedFilters}
          setHasAppliedFilters={setHasAppliedFilters}
          reportsToUsers={reportsToUsers}
          handleSearch={(clearSearch?: boolean) => {
            setPage(0);
          }}
          applyFilters={(clearFilters?: boolean) => {
            if (clearFilters) {
              setFilters({
                roleId: '',
                reportsToAccountName: ''
              });
            } else {
              setPage(0);
              setFilters({
                ...filters,
                roleId: selectedRoleId || undefined,
                reportsToAccountName: selectedReportsToAccount || undefined
              });
            }
          }}
          searchValue={searchValue}
          setSearch={setSearch}
          setSearchedValue={setSearchedValue}
          setPage={setPage}
        />
        {searchedValue && (
          <SearchSection
            searchedValue={searchedValue}
            handleClearSearch={() => {
              setSearch('');
              setSearchedValue('');
              setPage(0);
              setHasAppliedFilters(false);
            }}
          />
        )}
        <Table
          columns={columns}
          data={managePlaceOrderUsers}
          isLoading={isLoadingManagePlaceOrderUsers || isLoadingRoles}
          hidePagination
          ResponsiveComponent={isMobile ? MobileUserRoles : undefined}
          mobileProps={{
            handleEdit: canEdit ? (val: IManagePlaceOrderUser) => handleEditUsers(val) : null,
            handleDelete:
              isGlobalAdmin || isAdmin
                ? (val: IManagePlaceOrderUser) => handleDelete(val.accountName)
                : null
          }}
        />
        {!isLoadingManagePlaceOrderUsers && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
      </OrderManagementPage>

      <UserRolesUserModal
        open={isUserModalOpen}
        isLoadingUsers={isLoadingManagePlaceOrderUsers}
        onClose={() => {
          showUserModal(false);
          setTimeout(() => {
            setCurrentUser(null);
          }, 500);
        }}
        roles={roles}
        fetchUsers={fetchUsers}
        users={managePlaceOrderUsers}
        currentUser={currentUser}
        fetchRoleUsers={fetchRoles}
      />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  pageTitleWrapper: {
    '@media (min-width: 768px)': {
      flex: 1
    }
  },

  infoWrapper: {
    '@media (min-width: 768px)': {
      textAlign: 'right'
    }
  },
  addUserWrapper: {
    marginBottom: theme.spacing(1)
  },
  addUserButton: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  editButton: {
    textAlign: 'right'
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  }
}));
